import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  PowerSettingsNew as ToggleIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import service, {
  DificuldadeMissao,
  Missao,
  MissaoPayload,
  MissaoStats,
  TipoEventoMissao,
} from '../../services/marketing-missoes.service';
import { PageHeader, StatCard, EmptyState } from '../../components/Marketing';
import {
  EmojiEvents,
  PlayCircleOutline,
  CheckCircleOutline,
  StarsOutlined,
} from '@mui/icons-material';

const EVENTO_LABELS: Record<TipoEventoMissao, string> = {
  pagamento: 'Pagamento Wallet',
  topup: 'Topup',
  sisp_payment: 'Pagamento SISP',
  compra_app: 'Compra de Mini-App',
  ticket_purchase: 'Compra de Bilhete',
  recarga: 'Recarga Telemóvel',
  referral: 'Referral',
  login: 'Login',
  custom: 'Custom',
};

const DIFIC_COLORS: Record<DificuldadeMissao, 'success' | 'warning' | 'error'> = {
  facil: 'success',
  media: 'warning',
  dificil: 'error',
};

const EMPTY: MissaoPayload = {
  titulo: '',
  descricao: '',
  icone: '🎯',
  tipo_evento: 'pagamento',
  objetivo: 3,
  pontos_recompensa: 30,
  bonus_cve: 0,
  categoria: 'geral',
  dificuldade: 'facil',
  max_completar_por_user: 1,
  ativo: true,
  ordem: 0,
};

export default function Missoes() {
  const [rows, setRows] = useState<Missao[]>([]);
  const [stats, setStats] = useState<MissaoStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Missao | null>(null);
  const [form, setForm] = useState<MissaoPayload>(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, s] = await Promise.all([service.list(), service.stats().catch(() => null)]);
      setRows(list);
      setStats(s);
    } catch (e: any) {
      setToast({ msg: e?.message || 'Erro', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setDialogOpen(true);
  };

  const openEdit = (m: Missao) => {
    setEditing(m);
    setForm({
      titulo: m.titulo,
      descricao: m.descricao || '',
      icone: m.icone,
      tipo_evento: m.tipo_evento,
      objetivo: m.objetivo,
      pontos_recompensa: m.pontos_recompensa,
      bonus_cve: m.bonus_cve,
      categoria: m.categoria,
      dificuldade: m.dificuldade,
      data_inicio: m.data_inicio?.slice(0, 16) || '',
      data_fim: m.data_fim?.slice(0, 16) || '',
      max_completar_por_user: m.max_completar_por_user,
      ativo: m.ativo === 1,
      ordem: m.ordem,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.titulo.trim()) {
      setToast({ msg: 'Título obrigatório', severity: 'error' });
      return;
    }
    setSaving(true);
    try {
      if (editing) await service.update(editing.id, form);
      else await service.create(form);
      setToast({ msg: editing ? 'Atualizada' : 'Criada', severity: 'success' });
      setDialogOpen(false);
      await load();
    } catch (e: any) {
      setToast({ msg: e?.message || 'Erro', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (m: Missao) => {
    try {
      await service.toggle(m.id);
      await load();
    } catch (e: any) {
      setToast({ msg: e?.message || 'Erro', severity: 'error' });
    }
  };

  const remove = async (m: Missao) => {
    if (!window.confirm(`Eliminar missão "${m.titulo}"?`)) return;
    try {
      await service.remove(m.id);
      setToast({ msg: 'Eliminada', severity: 'success' });
      await load();
    } catch (e: any) {
      setToast({ msg: e?.message || 'Erro', severity: 'error' });
    }
  };

  return (
    <Box>
      <PageHeader
        title="Missões & Gamificação"
        subtitle="Desafios para aumentar retenção — auto-tracking via pipeline de pontos"
        icon={<EmojiEvents />}
        actions={
          <>
            <Button startIcon={<RefreshIcon />} onClick={load}>Atualizar</Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Nova Missão</Button>
          </>
        }
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        Ao creditar pontos a um utilizador (pagamento, topup, etc.), o progresso em missões ativas
        que correspondam ao <b>tipo de evento</b> é incrementado automaticamente.
      </Alert>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3, mb: 3 }}>
        <StatCard icon={<EmojiEvents />} label="Total de missões" value={stats?.total ?? '—'} color="primary" />
        <StatCard icon={<PlayCircleOutline />} label="Ativas" value={stats?.ativas ?? '—'} color="success" />
        <StatCard icon={<CheckCircleOutline />} label="Completadas" value={stats?.completadas_total ?? '—'} color="info" />
        <StatCard icon={<StarsOutlined />} label="Pontos distribuídos" value={stats?.pontos_distribuidos ?? '—'} color="warning" />
      </Box>

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Missão</TableCell>
                <TableCell>Evento</TableCell>
                <TableCell align="right">Objetivo</TableCell>
                <TableCell align="right">Recompensa</TableCell>
                <TableCell>Dificuldade</TableCell>
                <TableCell align="center">Participação</TableCell>
                <TableCell align="center">Ativa</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ border: 0 }}>
                    <EmptyState
                      icon={<EmojiEvents />}
                      title="Sem missões"
                      description="Crie desafios como 'faz 3 pagamentos esta semana → 30 pts'. O progresso é incrementado automaticamente sempre que o utilizador realiza o evento."
                      action={
                        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                          Nova Missão
                        </Button>
                      }
                    />
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((m) => {
                  const partic = m.total_participantes || 0;
                  const compl = m.total_completaram || 0;
                  const pct = partic ? Math.round((compl / partic) * 100) : 0;
                  return (
                    <TableRow key={m.id} hover>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="h6">{m.icone}</Typography>
                          <Box>
                            <Typography variant="body2" fontWeight={500}>{m.titulo}</Typography>
                            {m.descricao && (
                              <Typography variant="caption" color="text.secondary">
                                {m.descricao}
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={EVENTO_LABELS[m.tipo_evento]} variant="outlined" />
                      </TableCell>
                      <TableCell align="right">{m.objetivo}×</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={500}>{m.pontos_recompensa} pts</Typography>
                        {m.bonus_cve > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            + {m.bonus_cve} CVE
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={m.dificuldade} color={DIFIC_COLORS[m.dificuldade]} />
                      </TableCell>
                      <TableCell align="center" sx={{ minWidth: 140 }}>
                        <Typography variant="caption">{compl} / {partic}</Typography>
                        <LinearProgress variant="determinate" value={pct} sx={{ mt: 0.5 }} />
                      </TableCell>
                      <TableCell align="center">
                        <Switch size="small" checked={m.ativo === 1} onChange={() => toggle(m)} />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => openEdit(m)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" color="error" onClick={() => remove(m)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>{editing ? 'Editar Missão' : 'Nova Missão'}</DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          <Stack spacing={3}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'auto 1fr' }, gap: 3, alignItems: 'start' }}>
              <TextField
                label="Ícone"
                value={form.icone}
                onChange={(e) => setForm({ ...form, icone: e.target.value })}
                sx={{ width: { xs: '100%', md: 120 } }}
              />
              <TextField
                autoFocus
                fullWidth
                label="Título"
                placeholder="Ex: Faz 3 pagamentos esta semana"
                value={form.titulo}
                onChange={(e) => setForm({ ...form, titulo: e.target.value })}
              />
            </Box>

            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Descrição"
              placeholder="Ex: Completa 3 pagamentos com wallet e ganha 30 pontos de fidelidade"
              value={form.descricao}
              onChange={(e) => setForm({ ...form, descricao: e.target.value })}
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Tipo de evento</InputLabel>
                <Select
                  label="Tipo de evento"
                  value={form.tipo_evento}
                  onChange={(e) => setForm({ ...form, tipo_evento: e.target.value as TipoEventoMissao })}
                >
                  {Object.entries(EVENTO_LABELS).map(([k, v]) => (
                    <MenuItem key={k} value={k}>{v}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Dificuldade</InputLabel>
                <Select
                  label="Dificuldade"
                  value={form.dificuldade || 'facil'}
                  onChange={(e) => setForm({ ...form, dificuldade: e.target.value as DificuldadeMissao })}
                >
                  <MenuItem value="facil">Fácil</MenuItem>
                  <MenuItem value="media">Média</MenuItem>
                  <MenuItem value="dificil">Difícil</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3 }}>
              <TextField
                fullWidth
                type="number"
                label="Objetivo (ocorrências)"
                value={form.objetivo}
                onChange={(e) => setForm({ ...form, objetivo: Number(e.target.value) })}
                inputProps={{ min: 1 }}
              />
              <TextField
                fullWidth
                type="number"
                label="Pontos recompensa"
                value={form.pontos_recompensa}
                onChange={(e) => setForm({ ...form, pontos_recompensa: Number(e.target.value) })}
              />
              <TextField
                fullWidth
                type="number"
                label="Bónus CVE (opcional)"
                value={form.bonus_cve ?? 0}
                onChange={(e) => setForm({ ...form, bonus_cve: Number(e.target.value) })}
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Data início (opcional)"
                InputLabelProps={{ shrink: true }}
                value={form.data_inicio || ''}
                onChange={(e) => setForm({ ...form, data_inicio: e.target.value })}
              />
              <TextField
                fullWidth
                type="datetime-local"
                label="Data fim (opcional)"
                InputLabelProps={{ shrink: true }}
                value={form.data_fim || ''}
                onChange={(e) => setForm({ ...form, data_fim: e.target.value })}
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr auto' }, gap: 3, alignItems: 'center' }}>
              <TextField
                fullWidth
                label="Categoria"
                placeholder="Ex: geral, pagamentos, food"
                value={form.categoria}
                onChange={(e) => setForm({ ...form, categoria: e.target.value })}
              />
              <TextField
                fullWidth
                type="number"
                label="Ordem"
                value={form.ordem ?? 0}
                onChange={(e) => setForm({ ...form, ordem: Number(e.target.value) })}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.ativo !== false}
                    onChange={(e) => setForm({ ...form, ativo: e.target.checked })}
                  />
                }
                label="Ativa"
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? 'A guardar…' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={3500} onClose={() => setToast(null)}>
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}

// Ícone não usado removido; evita warning do TS
void ToggleIcon;
